import React, { Suspense, useRef, useState, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import {  useThree, useFrame } from '@react-three/fiber'


function Particles({ count }) {
    const mesh = useRef()
    const { mouse } = useThree()
  
    const dummy = useMemo(() => new THREE.Object3D(), [])
    
    const particles = useMemo(() => {
      const temp = []
      for (let i = 0; i < count; i++) {
        const t = Math.random() * 10
        const factor = 1 + Math.random()
        const speed = 0.01 + Math.random() / 200
        const start = new THREE.Vector3(Math.random() * 20 - 10,
                                        Math.random() * 20 - 10,
                                        Math.random() * 20 - 10);
        temp.push({ t, factor, speed, start, x: 0, y: 0 })
      }
      return temp
    }, [count])
    
    useFrame(({clock}) => {
      
      // Go through each particle
      let pos = 1;
      if(clock.elapsedTime > 14.5 && clock.elapsedTime < 18)
        pos = Math.max(15 - clock.elapsedTime, 0);
      else if(clock.elapsedTime > 18)
        pos = Math.min(clock.elapsedTime - 18, 1);

      particles.forEach((particle, i) => {
        let { t, factor, speed, start} = particle
        t = particle.t += speed / 2
        const a = Math.cos(t) + Math.sin(t * 1) / 10
        const b = Math.sin(t) + Math.cos(t * 2) / 10
        const s = (Math.cos(t*10)/5+1.5) / 8
        particle.x += (mouse.x - particle.x) * 0.01
        particle.y += (mouse.y * -1 - particle.y) * 0.01
        // Change position
        dummy.position.set(
          ((particle.x / 10) * a + start.x + Math.cos((t / 10) * factor) + (Math.sin(t * 1) * factor) / 10) * pos,
          ((particle.y / 10) * b + start.y + Math.sin((t / 10) * factor) + (Math.cos(t * 2) * factor) / 10) * pos,
          ((particle.y / 10) * b + start.z + Math.cos((t / 10) * factor) + (Math.sin(t * 3) * factor) / 10) * pos
        )
        dummy.scale.set(s, s, s)
        dummy.rotation.set(s, s, s)
        dummy.updateMatrix()
        // And apply the matrix to the instanced mesh
        mesh.current.setMatrixAt(i, dummy.matrix)
      })
      mesh.current.instanceMatrix.needsUpdate = true
    })
    return (
      <>
        <instancedMesh ref={mesh} args={[null, null, count]}>
          <sphereGeometry attach="geometry" args={[0.08]} />
          <meshPhongMaterial attach="material" color="#aaaaff" />
        </instancedMesh>
      </>
    )
  }
  
  
  
function InnerCircle({ count }) {
    const mesh = useRef()
    const { mouse, camera } = useThree()
  
    const dummy = useMemo(() => new THREE.Object3D(), [])
    
    const particles = useMemo(() => {
      const temp = []
      for (let i = 0; i < count * count; i++) {
        const t = Math.random() * 10;
        const speed = 0.01 + Math.random() / 200;
        const rand = Math.random()*0.3;
        const start = new THREE.Vector3(rand, rand, rand);
        temp.push({ t, speed, rand, start, x: 0, y: 0 })
      }
      return temp
    }, [count])
    
    useFrame(({clock}) => {
      // camera.position.set(mouse.x, mouse.y, 5);
      // camera.lookAt(new THREE.Vector3(0,0,0));
      
      // Go through each particle
  
      let pos = 0;
      if(clock.elapsedTime > 18) pos = Math.min(1, clock.elapsedTime - 18);

      for(let i=0; i < count; i++) {
        let rand = (Math.random() * 0.3 + 0.9) * pos;
        for(let j=0; j < count; j++) {
          let cur = i * count + j;
          let r = Math.sin(rand);
          
          const a = Math.cos(Math.PI * 2 * i / count) * Math.sin(Math.PI * 2 * j / count) * r;
          const b = Math.sin(Math.PI * 2 * i / count) * Math.sin(Math.PI * 2 * j / count) * r;
          const s = Math.cos(Math.PI * 2 * j / count) * r;
          // Change position
          dummy.position.set(a,b,s);
          // dummy.scale.set(0.2,0.2,0.2);
          dummy.scale.copy(particles[cur].start);
            // dummy.rotation.set(s, s, s)
          dummy.updateMatrix();
          // And apply the matrix to the instanced mesh
          mesh.current.setMatrixAt(cur, dummy.matrix)
        }
      }
      
      mesh.current.instanceMatrix.needsUpdate = true
    })
    return (
      <>
        <instancedMesh ref={mesh} args={[null, null, count * count]}>
          <dodecahedronBufferGeometry attach="geometry" args={[0.05, 0]} />
          <meshBasicMaterial attach="material" color="#44eeff" />
        </instancedMesh>
      </>
    )
}



function trailFactory(start, end, amount) {
    let points = [];

    function rec(start, end, depth) {

      let distance = start.distanceTo(end);
      let change = () => { return (Math.random() * distance - distance / 2) / depth / 2 * amount; };
      let offset = new THREE.Vector3(change(), change(), change());
      let midPoint = new THREE.Vector3().copy(start).add(end).multiplyScalar(0.5).add(offset);

      if(depth < 4) {
          rec(start, midPoint, depth + 1);
          rec(midPoint, end, depth + 1);
          if(Math.random() > 0.3) 
            rec(midPoint, end.clone().add(new THREE.Vector3(change(), change(), change())), depth + 1);
      } else {
          points.push(start);
          points.push(midPoint);
          // points.push(end)
      }
    }


    rec(start, end, 1);
    points.life = Math.random() * 3;
    return points;
}

function movement(points) {
  let distance = points[0].distanceTo(points[points.length - 1])*0.05;
  points.forEach((point, i) => {
    point.add(new THREE.Vector3((Math.random() - 0.5)*0.1*distance, (Math.random() - 0.5)*0.1*distance, (Math.random() - 0.5)*0.1*distance));
  });
}

function getPointOnSphere(r) {
  let phi = Math.random(), theta = Math.random();
  const a = Math.cos(Math.PI * 2 * phi) * Math.sin(Math.PI * 2 * theta) * r;
  const b = Math.sin(Math.PI * 2 * phi) * Math.sin(Math.PI * 2 * theta) * r;
  const s = Math.cos(Math.PI * 2 * theta) * r;
  return new THREE.Vector3(a, b, s);
}

function Trails({ count }) {
    const mesh = useRef();
    const { mouse, camera, scene } = useThree();
  
    const dummy = useMemo(() => new THREE.Object3D(), []);
    let points = [trailFactory(new THREE.Vector3(0,0,0), new THREE.Vector3(0, 0, 0), 1)];
    for(let i=0; i < 5; i++) {
      let pointOnSphere = getPointOnSphere(0.5);
      points.push(trailFactory(pointOnSphere, pointOnSphere.clone().multiplyScalar(1), 1));
    }
    let lastTime = 0;
    
    const particles = useMemo(() => {
      const temp = []
      for (let i = 0; i < count; i++) {
        const rand = Math.random();
        let life = 0.5;
        temp.push({ rand, life });
      }
      return temp;
    }, [count]);

    useEffect(() => {
        if(!mesh.current.changed) {
          camera.position.set(0,0, 7);
          camera.lookAt(0,0,0);
            mesh.current.changed = true;
            mesh.current.geometry.rotateX(Math.PI / 2);
            mesh.current.geometry.translate(0,0,0.5);
        }
    })
    
    useFrame(({clock, raycaster}) => {
      // camera.position.set(mouse.x, mouse.y, 5);
      // camera.lookAt(new THREE.Vector3(0,0,0));
      if(clock.elapsedTime < 4) return;
      if(clock.elapsedTime - lastTime > Math.random() * 2) 
      {
        scene.getObjectByName('plane').lookAt(camera.position)
        raycaster.setFromCamera(mouse, camera);

        const intersects = raycaster.intersectObjects([scene.getObjectByName('plane')], false );
        if(intersects.length > 0)
        {
          for(let i=1; i < points.length; i++) {
            points[i].life -= clock.elapsedTime - lastTime;
            if(points[i].life < 0) {
              let pointOnSphere = getPointOnSphere(Math.min(0.5, Math.max(0.1, 20 - clock.elapsedTime)));
              let endPoint = pointOnSphere.clone().multiplyScalar(Math.min(clock.elapsedTime - 4, 8));
              points[i] = trailFactory(pointOnSphere, endPoint, 0.5);
              points[i].life = Math.random() * 3;

            }
          }
          if(clock.elapsedTime > 5 && clock.elapsedTime < 20)
            points[0] = trailFactory(new THREE.Vector3(0,0,0), intersects[0].point, 1);
          else points[0] = [new THREE.Vector3(0,0,0),new THREE.Vector3(0,0,0)];
          lastTime = clock.elapsedTime;
        }

      }
      else {
        points.forEach(point => movement(point));
      }
      
      // Go through each particle
      let index = 0;
      let size = 0.2;
      if(clock.elapsedTime < 5) size = 0;
      else if(clock.elapsedTime > 14.5 && clock.elapsedTime < 20)
        size = Math.max((15 - clock.elapsedTime) * 0.5, 0);
      particles.forEach((particle, i) => {
        let { rand } = particle;
        
        // particle.life -= 0.1;
        if(particle.life < 0) particle.life = 3;
        // Change position
        points.forEach(points => {
          if(points[i+1]){
            dummy.position.copy(points[i]);
            dummy.lookAt(points[i+1]);
            dummy.scale.set(  (Math.sin(-clock.elapsedTime * 5 + i/10) + 1) * size, 
                              (Math.sin(-clock.elapsedTime * 5 + i/10) + 1) * size, 
                              points[i].distanceTo(points[i+1]));
          }
          index++;
          
          dummy.updateMatrix()
          // And apply the matrix to the instanced mesh
          mesh.current.setMatrixAt(index, dummy.matrix)
        })
        
      })
      mesh.current.instanceMatrix.needsUpdate = true;
    })
    return (
      <>
        <instancedMesh ref={mesh} args={[null, null, count]} changed={false}>
          <cylinderGeometry attach="geometry" args={[0.05, 0.05]}/>
          <meshBasicMaterial attach="material" color="#05a0f0" />
        </instancedMesh>
      </>
    )
  }

  
  
  export {InnerCircle, Particles, Trails};